import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';

import { MediaPlayerService } from '../../services/media-player/media-player.service';

/**
 * Host canvas for the `@foobar404/wave` visualiser.
 *
 * The canvas backing store must match its CSS box, otherwise the library draws
 * into the default 300x150 buffer and the browser stretches that to whatever
 * size the layout gives us. We size it here (DPR-aware) and re-size on layout
 * changes, and hand the element straight to the player service.
 */
@Component({
  selector: 'audiowaveform-component',
  standalone: true,
  template: '<canvas #canvas aria-hidden="true"></canvas>',
  styles: [
    `:host { display: block; width: 100%; height: 100%; line-height: 0; }
     canvas { display: block; width: 100%; height: 100%; }`,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioWaveformComponent implements AfterViewInit, OnDestroy {
  private readonly zone = inject(NgZone);

  @ViewChild('canvas', { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  private observer?: ResizeObserver;

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    MediaPlayerService.audioWaveformCanvasElement = canvas;
    this.syncCanvasSize();

    if (typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        this.observer = new ResizeObserver(() => this.syncCanvasSize());
        this.observer.observe(canvas);
      });
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (MediaPlayerService.audioWaveformCanvasElement === this.canvasRef.nativeElement) {
      MediaPlayerService.audioWaveformCanvasElement = undefined;
    }
  }

  /** Match the pixel buffer to the laid-out size so nothing is stretched. */
  private syncCanvasSize(): void {
    const canvas = this.canvasRef.nativeElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.round(canvas.clientWidth * dpr);
    const height = Math.round(canvas.clientHeight * dpr);

    if (width > 0 && height > 0 && (canvas.width !== width || canvas.height !== height)) {
      canvas.width = width;
      canvas.height = height;
    }
  }
}
